import {
  bigNumberToNumber,
  getTotalWeightNumber,
  stringToBigNumber,
} from "@/common/helpers/bignumber"
import type {
  Governor,
  GovernorParameters,
  Maybe,
  VoteStats,
} from "@/query/graphql"
import { GovernorType, VoteType } from "@/query/graphql"

export type GovernorVotesCount = Pick<Governor, "type"> & {
  parameters?: Pick<GovernorParameters, "countingMode">
}

export const getQuorumVoteTypeFilter = ({
  governor,
  isIncludePendingVotes = false,
}: {
  governor?: GovernorVotesCount
  isIncludePendingVotes?: boolean
}) => {
  const countingMode = governor?.parameters?.countingMode ?? undefined

  if (countingMode) {
    // countingMode could have a value like
    // "support=bravo&quorum=for,abstain&params=fractional"
    // So we should get the values in quorum: "for,abstain"
    const quorumMatch = countingMode.match(/quorum=([^&]+)/)
    const quorumMatchString = quorumMatch ? quorumMatch[1] : undefined

    if (quorumMatchString) {
      // Convert countingMode string to array of VoteType enum values
      const voteTypes = quorumMatchString
        .split(",")
        .map((type) => {
          switch (type.trim()) {
            case "for":
              return VoteType.For
            case "abstain":
              return VoteType.Abstain
            case "against":
              return VoteType.Against
            default:
              return null
          }
        })
        .filter((type): type is NonNullable<typeof type> => type !== null)

      // If we have pending votes enabled, add the corresponding pending vote types
      if (isIncludePendingVotes) {
        const pendingVoteTypes = quorumMatchString
          .split(",")
          .map((type) => {
            switch (type.trim()) {
              case "pendingfor":
                return VoteType.Pendingfor
              case "pendingagainst":
                return VoteType.Pendingagainst
              case "pendingabstain":
                return VoteType.Pendingabstain
              default:
                return null
            }
          })
          .filter((type): type is NonNullable<typeof type> => type !== null)

        return [...voteTypes, ...pendingVoteTypes]
      }

      return voteTypes
    }
  }

  // If no countingMode is available, then get the right ones using governor's type
  const governorType = governor?.type ?? undefined

  if (isIncludePendingVotes) {
    return governorType === GovernorType.Governoralpha ||
      governorType === GovernorType.Governorbravo
      ? [VoteType.For, VoteType.Pendingfor]
      : [
          VoteType.For,
          VoteType.Abstain,
          VoteType.Pendingfor,
          VoteType.Pendingabstain,
        ]
  }

  return governorType === GovernorType.Governoralpha ||
    governorType === GovernorType.Governorbravo
    ? [VoteType.For]
    : [VoteType.For, VoteType.Abstain]
}

const getProposalVotesWeight = (
  governor: GovernorVotesCount,
  voteStats?:
    | {
        votesCount: string
        votersCount: number
        type: VoteType
      }[]
    | null,
) => {
  const quorumVoteTypeFilter = getQuorumVoteTypeFilter({ governor })

  const weights = voteStats
    ? voteStats
        ?.filter((voteStat) => quorumVoteTypeFilter.includes(voteStat.type))
        .map(({ votesCount }) => `${votesCount}`)
    : []

  return weights
}

type VoteStatsQuorum = Pick<VoteStats, "votesCount" | "votersCount" | "type">

export function wasDefeatedByQuorum(
  governor: GovernorVotesCount,
  quorum: string,
  decimals: number,
  voteStats?: Maybe<VoteStatsQuorum[]>,
) {
  const weights = getProposalVotesWeight(governor, voteStats)

  const totalWeight = weights
    ? getTotalWeightNumber(weights, decimals)
    : undefined
  const bigQuorum = stringToBigNumber(quorum)
  const quorumWeight = bigNumberToNumber(bigQuorum, decimals)

  if (!totalWeight) return false

  return totalWeight < quorumWeight
}
